// Angular
import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
} from '@angular/forms';
// PrimeNG
import { CheckboxChangeEvent, CheckboxModule } from 'primeng/checkbox';
// Validators
import { TermsOfServiceValidator } from './validators/terms-of-service.validator';
import { ControlsOf } from '@shared/models-v3/controls-of.type';

const NG_MODULES = [ReactiveFormsModule];
const PRIME_MODULES = [CheckboxModule];

@Component({
  selector: 'app-terms-of-service-input',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES],
  templateUrl: './terms-of-service-input.component.html',
  styleUrl: './terms-of-service-input.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TermsOfServiceInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TermsOfServiceInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsOfServiceInputComponent
  implements ControlValueAccessor, Validator
{
  private onChange: (value: boolean) => void = () => {};
  private onTouch: () => void = () => {};

  form!: FormGroup<ControlsOf<{ termsOfService: boolean }>>;
  private readonly fb = inject(NonNullableFormBuilder);

  get termsOfServiceField(): FormControl<boolean> {
    return this.form.controls.termsOfService;
  }

  constructor() {
    this.buildForm();
  }

  private buildForm() {
    this.form = this.fb.group({
      termsOfService: [false, TermsOfServiceValidator.isValid],
    });
  }

  //#region Event handlers
  onTermsOfServiceChange(value: CheckboxChangeEvent): void {
    this.onChange(value.checked);
  }

  onTermsOfServiceBlur(): void {
    this.onTouch();
  }
  //#endregion

  //#region ControlValueAccessor
  /**
   * Actualizar el valor del input cuando el formulario padre cambia.
   * + Angular lo invoca automáticamente cuando se actualiza el valor del FormControl asociado con el componente.
   * + Es invocado automáticamente por Angular y no necesitas llamarlo manualmente.
   * + Tu implementación debería actualizar el valor del input en la UI con el valor proporcionado.
   * @param value The new value for the control.
   * @returns void
   * @example
   * writeValue(value: any): void {
   *   if (value) {
   *     this.form.get('documentId')?.setValue(value);
   *   }
   * }
   **/
  writeValue(value: boolean): void {
    this.termsOfServiceField?.setValue(value);
  }

  /**
   * Registrar una función que debe ser invocada cada vez que el valor del input cambie.
   * + Esta función se utiliza para notificar al formulario padre que el valor del input ha cambiado.
   * + No se invoca directamente.
   * + Debes guardar la referencia de la función fn proporcionada y llamarla cuando el valor del input cambie.
   * @param fn The callback function.
   * @returns void
   * @example
   * registerOnChange(fn: any): void {
   *   this.onChange = fn;
   * }
   * onInput(event: Event): void {
   *   const value = (event.target as HTMLInputElement).value;
   *   this.onChange(value);  // Notificar al formulario padre
   * }
   **/
  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  /**
   * Registrar una función que debe ser invocada cuando el control es "tocado" (es decir, cuando el usuario interactúa con él).
   * + Se invoca cuando se necesita notificar que el control fue tocado, generalmente en eventos como blur.
   * @param fn The callback function.
   * @returns void
   * @example
   * registerOnTouched(fn: any): void {
   *   this.onTouched = fn;
   * }
   * onBlur(): void {
   *   this.onTouched();  // Notificar que el control fue tocado
   * }
   **/
  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }
  // #endregion

  // #region Validator
  /**
   * Validar el control síncronamente.
   * @returns The validation errors or null.
   * */
  validate(): ValidationErrors | null {
    return this.termsOfServiceField?.errors || null;
  }
  // #endregion
}
